import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { SignupStep1Widget } from './SignupStep1Widget';
import { SignupStep2Widget } from './SignupStep2Widget';
import { SignupStep3Widget } from './SignupStep3Widget';
import { useTokenStore } from '../../hooks/useTokenStore';
import { useSystem } from '../../hooks/useSystem';
import { SignupActionSource } from '@journeyapps-platform/types-flight-log';
import { SignupMobileWidget } from './SignupMobileWidget';
import { LoaderWidget } from '../../widgets/LoaderWidget';
import { useNavigate } from 'react-router-dom';
import { AccountsV5Client } from '@journeyapps-platform/sdk-accounts-hub';
import { WebNetworkClient } from '@journeyapps-platform/sdk-common';
import { getProductBasedOnFeatures, Product, PRODUCT_CONFIG_MAP } from '@journeyapps-platform/types-accounts';
import { ENV } from '../../env';
import { ProductContextWidget } from '../../widgets/product/context';
import { GetSignupUserResponse } from '@journeyapps-platform/types-accounts-hub';

export enum Step {
  STEP_1 = 'step1',
  STEP_2 = 'step2',
  STEP_3 = 'step3',
  MOBILE = 'mobile'
}

export interface SignupPayload {
  firstName: string;
  lastName: string;
  orgName: string;
  password: string;
  mobile: string;
  acceptTerms: boolean;
  step: Step;
}

export const SignupPage = () => {
  const system = useSystem();
  const navigate = useNavigate();
  const tokenStore = useTokenStore();
  const [hubClient, setHubClient] = useState<AccountsV5Client<WebNetworkClient> | null>(null);
  const [user, setUser] = useState<GetSignupUserResponse>();
  const [product, setProduct] = useState<Product>();

  const [payload, setPayload] = useState<Partial<SignupPayload>>({
    acceptTerms: false,
    firstName: '',
    lastName: '',
    password: '',
    orgName: '',
    step: Step.STEP_1
  });

  // load client
  useEffect(() => {
    const token = new URL(window.location.href).searchParams.get('token') || '';
    if (!token) {
      if (system.context.DEV) {
        console.error('No user token found');
      } else {
        navigate('/free-trial', {
          replace: true
        });
      }
    }
    tokenStore.setToken(token);
    setHubClient(system.clientProvisioner.provisionHubClientFromTrialToken(token));
  }, []);

  // load user
  useEffect(() => {
    hubClient?.getSignupUser().then((u) => {
      setUser(u);
    });
  }, [hubClient]);

  // setup initial payload
  useEffect(() => {
    if (!user || !hubClient) {
      return;
    }
    hubClient!
      .listApps({
        org_id: user!.org_id
      })
      .then((apps) => {
        setProduct(getProductBasedOnFeatures(apps.objects[0]?.features || []));
      });
  }, [user, hubClient]);

  // change title based on product
  useEffect(() => {
    if (!product) {
      return;
    }
    const product_config = PRODUCT_CONFIG_MAP[product];
    document.title = `${product_config.product_name} | Finish Sign-up`;
  }, [product]);

  const completeSignup = useCallback(
    async (payload: Partial<SignupPayload>) => {
      try {
        const response = await system.accountsPortalClient.completeSignup({
          first_name: payload.firstName!,
          last_name: payload.lastName!,
          password: payload.password!,
          phone_number: payload.mobile,
          id: user!.id!,
          ...(user!.new_organization
            ? {
                org_name: payload.orgName
              }
            : {})
        });

        // We use the already configured flight-logger (still set to signup-step3)
        await system.flightLogger.logAction({ source: SignupActionSource.SYSTEM, action_id: 'successful-signup' });
        await system.flightLogger.flush();

        if (!system.context.isMobile) {
          window.location = response.url as any;
        } else {
          setPayload({
            ...payload,
            step: Step.MOBILE
          });
        }
      } catch (ex) {
        console.error('Failed to complete signup: ', ex);
        system.flightLogger.logAction({ source: SignupActionSource.SYSTEM, action_id: 'signup-failed' });
      }
    },
    [user]
  );

  if (!product || !user?.email) {
    return <LoaderWidget />;
  }

  if (payload.step === Step.STEP_1) {
    return (
      <ProductContextWidget product={product!}>
        <SignupStep1Widget
          user={user!}
          payload={payload}
          next={async (p) => {
            if (ENV.FORCE_MOBILE_VERIFY === 'true') {
              return setPayload({
                ...payload,
                ...p,
                step: Step.STEP_2
              });
            }
            // if mobile number is not enforced, we complete signup at this step
            await completeSignup({
              ...payload,
              ...p
            });
          }}
        />
      </ProductContextWidget>
    );
  }
  if (payload.step === Step.STEP_2) {
    return (
      <ProductContextWidget product={product!}>
        <SignupStep2Widget
          user={user!}
          payload={payload}
          next={(mobileNumber) => {
            setPayload({
              ...payload,
              step: Step.STEP_3,
              mobile: mobileNumber
            });
          }}
        />
      </ProductContextWidget>
    );
  }
  if (payload.step == Step.STEP_3) {
    return (
      <ProductContextWidget product={product!}>
        <SignupStep3Widget
          user={user!}
          payload={payload}
          back={() => {
            setPayload({
              ...payload,
              step: Step.STEP_2
            });
          }}
          next={() => {
            completeSignup(payload);
          }}
        />
      </ProductContextWidget>
    );
  }
  return (
    <ProductContextWidget product={product!}>
      <SignupMobileWidget />
    </ProductContextWidget>
  );
};
